import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Button,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Chip,
} from "@mui/material";
import { Add, Edit, Delete, NotificationsActive, PlaylistAdd, DocumentScanner, People, ViewList } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import unclaimedPackageService from "../../services/unclaimedPackageService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import UnclaimedPackageDialog from "../../components/unclaimed/UnclaimedPackageDialog";
import BulkAddUnclaimedPackagesDialog from "../../components/unclaimed/BulkAddUnclaimedPackagesDialog";
import ScanUnclaimedMatchesDialog from "../../components/common/ScanUnclaimedMatchesDialog";
import { selectStatusOptions, selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";

const URL_SCHEMA = {
    statusFilter: { default: "" },
    search: { default: "" },
    page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
    rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const AdminUnclaimedPackages = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const statusOptions = useSelector(selectStatusOptions("unclaimedPackage"));
    const claimedKey = useSelector(selectKeyForRole("unclaimedPackage", "claimed"));

    useScrollRestoration();
    const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
    const { statusFilter, search, page, rowsPerPage } = urlState;
    // Local typing buffer, debounced into urlState.search below — seeded
    // from the URL so a restored search still shows in the field.
    const [searchInput, setSearchInput] = useState(urlState.search);

    const [packages, setPackages] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [scanDialogOpen, setScanDialogOpen] = useState(false);

    // "list" = flat paginated table (existing behavior). "customer" = every
    // unclaimed package grouped under its possible customer, with one
    // reminder button that emails all of that customer's packages together
    // instead of one email per package.
    const [viewMode, setViewMode] = useState("list");
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [sendingCustomerId, setSendingCustomerId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await unclaimedPackageService.getAll({
                status: statusFilter || undefined,
                search: search || undefined,
                page: page + 1,
                limit: rowsPerPage,
            });
            setPackages(data.data || []);
            setTotal(data.pagination?.total ?? data.total ?? 0);
        } catch {
            enqueueSnackbar("Failed to load unclaimed packages", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Debounced search — only fire once typing pauses, not on every
    // keystroke. Skipped when searchInput already matches the URL's
    // committed value — true right after mount — so restoring a search
    // from the URL doesn't also reset page back to 0.
    useEffect(() => {
        if (searchInput === search) return;
        const timer = setTimeout(() => {
            setUrlState({ search: searchInput, page: 0 });
        }, 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    useEffect(() => {
        if (viewMode === "list") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, search, page, rowsPerPage, viewMode]);

    const loadGroups = async () => {
        setGroupsLoading(true);
        try {
            const { data } = await unclaimedPackageService.getGroupedByCustomer();
            setGroups(data.data?.groups || []);
        } catch {
            enqueueSnackbar("Failed to load packages by customer", { variant: "error" });
        } finally {
            setGroupsLoading(false);
        }
    };

    useEffect(() => {
        if (viewMode === "customer") loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    const handleSendBulkReminder = async (customerId) => {
        setSendingCustomerId(customerId);
        try {
            const { data } = await unclaimedPackageService.sendBulkReminder(customerId);
            enqueueSnackbar(`Reminder sent covering ${data.data?.count ?? ""} package(s)`, { variant: "success" });
            loadGroups();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to send reminder", { variant: "error" });
        } finally {
            setSendingCustomerId(null);
        }
    };

    const handleSendReminder = async (pkg) => {
        try {
            await unclaimedPackageService.sendReminder(pkg._id);
            enqueueSnackbar("Reminder sent", { variant: "success" });
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to send reminder", { variant: "error" });
        }
    };

    const handleDelete = async (pkg) => {
        if (!window.confirm(`Delete unclaimed package ${pkg.trackingNumber}?`)) return;
        try {
            await unclaimedPackageService.remove(pkg._id);
            enqueueSnackbar("Package deleted", { variant: "success" });
            load();
        } catch {
            enqueueSnackbar("Failed to delete package", { variant: "error" });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Unclaimed Packages"
                subtitle="Packages received at the warehouse without a matching shipment"
                action={
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Tooltip title="Re-check every tracking number on undelivered shipments against unclaimed packages — catches anything missed by automatic matching">
                            <Button
                                variant="outlined"
                                startIcon={<DocumentScanner />}
                                onClick={() => setScanDialogOpen(true)}
                            >
                                Scan for Matches
                            </Button>
                        </Tooltip>
                        <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={() => setBulkDialogOpen(true)}>
                            Bulk Add
                        </Button>
                        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingPackage(null); setDialogOpen(true); }}>
                            Log Package
                        </Button>
                    </Box>
                }
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewMode}
                    onChange={(_, v) => v && setViewMode(v)}
                >
                    <ToggleButton value="list">
                        <ViewList fontSize="small" sx={{ mr: 0.75 }} />
                        List
                    </ToggleButton>
                    <ToggleButton value="customer">
                        <People fontSize="small" sx={{ mr: 0.75 }} />
                        By Customer
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {viewMode === "customer" ? (
                <Card>
                    {groupsLoading ? (
                        <SkeletonLoader rows={5} />
                    ) : groups.length === 0 ? (
                        <EmptyState
                            icon="📦"
                            title="No unclaimed packages with a possible customer"
                            description="Assign a possible customer to a package in the list view to have it show up here, grouped for a single combined reminder."
                        />
                    ) : (
                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {groups.map((group) => (
                                <Card key={group.customer._id} variant="outlined" sx={{ p: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                                        <Box>
                                            <Typography fontWeight={700}>
                                                {group.customer.firstName} {group.customer.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {group.customer.email}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Chip label={`${group.packages.length} package(s)`} size="small" />
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<NotificationsActive fontSize="small" />}
                                                disabled={sendingCustomerId === group.customer._id}
                                                onClick={() => handleSendBulkReminder(group.customer._id)}
                                            >
                                                Send Reminder to All
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        {group.packages.map((pkg) => (
                                            <Box
                                                key={pkg._id}
                                                sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: 13 }}
                                            >
                                                <Typography sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                    {pkg.trackingNumber}
                                                </Typography>
                                                {pkg.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {pkg.description}
                                                    </Typography>
                                                )}
                                                <StatusChip status={pkg.status} domain="unclaimedPackage" />
                                            </Box>
                                        ))}
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Card>
            ) : (
            <Card>
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setUrlState({ statusFilter: e.target.value, page: 0 })}
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {statusOptions.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Search"
                        placeholder="Tracking #, D/R, or description…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                </Box>

                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : packages.length === 0 ? (
                    <EmptyState
                        icon="📦"
                        title="No unclaimed packages"
                        description="Packages logged here have arrived without a matching shipment."
                        actionLabel="Log Package"
                        onAction={() => { setEditingPackage(null); setDialogOpen(true); }}
                    />
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tracking #</TableCell>
                                    <TableCell>D/R</TableCell>
                                    <TableCell>Carrier</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Possible Customer</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Resolved Shipment</TableCell>
                                    <TableCell>Received</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((pkg) => (
                                    <TableRow key={pkg._id} hover>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{pkg.trackingNumber}</TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{pkg.dockReceiptNumber || "—"}</TableCell>
                                        <TableCell>{pkg.carrier || "—"}</TableCell>
                                        <TableCell>{pkg.description || "—"}</TableCell>
                                        <TableCell>
                                            {pkg.possibleCustomer
                                                ? `${pkg.possibleCustomer.firstName} ${pkg.possibleCustomer.lastName}`
                                                : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={pkg.status} domain="unclaimedPackage" />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12 }}>
                                            {pkg.resolvedShipment ? (
                                                <Box
                                                    component="span"
                                                    onClick={() => navigate(`/admin/shipments/${pkg.resolvedShipment.trackingNumber}`)}
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        color: "primary.main",
                                                        cursor: "pointer",
                                                        "&:hover": { textDecoration: "underline" },
                                                    }}
                                                >
                                                    {pkg.resolvedShipment.trackingNumber}
                                                </Box>
                                            ) : (
                                                <Box component="span" sx={{ color: "text.secondary" }}>—</Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                            {pkg.receivedDate ? dayjs(pkg.receivedDate).format("MMM D, YYYY") : "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={pkg.possibleCustomer ? "Send Reminder" : "Select a customer first"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!pkg.possibleCustomer || pkg.status === claimedKey}
                                                        onClick={() => handleSendReminder(pkg)}
                                                    >
                                                        <NotificationsActive fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => { setEditingPackage(pkg); setDialogOpen(true); }}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => handleDelete(pkg)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setUrlState({ page: newPage })}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })}
                            rowsPerPageOptions={[10, 20, 50]}
                        />
                    </Box>
                )}
            </Card>
            )}

            <UnclaimedPackageDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={load}
                editingPackage={editingPackage}
            />

            <BulkAddUnclaimedPackagesDialog
                open={bulkDialogOpen}
                onClose={() => setBulkDialogOpen(false)}
                onCreated={load}
            />

            <ScanUnclaimedMatchesDialog
                open={scanDialogOpen}
                onClose={() => setScanDialogOpen(false)}
                onComplete={load}
            />
        </Box>
    );
};

export default AdminUnclaimedPackages;
